<!--
    Text Formatter Component
    - Custom element that provides text formatting, beautification, and transformation tools
    - Uses dual CodeMirror editors (input and output)
    - Supports JSON, XML, and TEXT operations
    - shadow: "none" disables Shadow DOM to inherit global Bulma styles
-->
<svelte:options customElement={{ tag: "text-formatter", shadow: "none" }} />

<script lang="ts">
    import { tick, untrack } from "svelte";
    import { createTextFormatterEditor } from "../../lib/text-formatter/CodeMirrorService";
    import { FormatterService } from "../../lib/text-formatter/FormatterService";
    import type { EditorView } from "@codemirror/view";
    import type { Language, Operation, JsonOperation, XmlOperation, TextOperation, Settings } from "../../lib/text-formatter/types";

    // ============================================================================
    // COMPONENT STATE
    // ============================================================================

    let inputEditorContainer: HTMLDivElement | undefined = $state();
    let outputEditorContainer: HTMLDivElement | undefined = $state();
    let inputEditor: EditorView | null = $state(null);
    let outputEditor: EditorView | null = $state(null);

    let settings: Settings = $state({
        language: "json",
        operation: "format",
        config: {
            indentSize: 2,
            sortKeys: false,
        },
    });

    let inputContent: string = $state("");
    let outputContent: string = $state("");
    let errorMessage: string = $state("");
    let isProcessing: boolean = $state(false);
    let currentLanguage: Language = $state("json");
    let lastProcessedInput: string = $state("");
    let isRecreatingEditors: boolean = $state(false);

    // ============================================================================
    // LIFECYCLE - Initialize editors
    // ============================================================================

    $effect(() => {
        if (inputEditorContainer && !inputEditor && !isRecreatingEditors) {
            inputEditor = createTextFormatterEditor({
                parent: inputEditorContainer,
                doc: inputContent,
                language: settings.language,
                readOnly: false,
                onChange: handleInputChange,
            });
        }
    });

    $effect(() => {
        if (outputEditorContainer && !outputEditor && !isRecreatingEditors) {
            outputEditor = createTextFormatterEditor({
                parent: outputEditorContainer,
                doc: outputContent,
                language: settings.language,
                readOnly: true,
            });
        }
    });

    // ============================================================================
    // REACTIVE EFFECTS
    // ============================================================================

    // Auto-format when input changes (with guard to prevent infinite loop)
    $effect(() => {
        if (inputContent && inputContent !== lastProcessedInput && !isProcessing) {
            lastProcessedInput = inputContent;
            performFormatting();
        } else if (!inputContent) {
            // Clear output when input is empty
            lastProcessedInput = "";
            outputContent = "";
            updateOutputEditor("");
        }
    });

    // Recreate editors when language changes
    $effect(() => {
        // Only track language changes, not editor references
        const lang = settings.language;

        if (lang !== currentLanguage) {
            // Update current language first to prevent re-entry
            currentLanguage = lang;

            // Use untrack to check editor existence without creating reactive dependency
            untrack(() => {
                if (inputEditor && outputEditor) {
                    recreateEditors();
                }
            });
        }
    });

    // ============================================================================
    // EDITOR MANAGEMENT
    // ============================================================================

    function handleInputChange(content: string): void {
        inputContent = content;
    }

    async function recreateEditors(): Promise<void> {
        // Set flag to prevent initialization effects from running
        isRecreatingEditors = true;

        const inputDoc = inputEditor?.state.doc.toString() || "";
        const outputDoc = outputEditor?.state.doc.toString() || "";

        // Destroy existing editors
        if (inputEditor) {
            inputEditor.destroy();
            inputEditor = null;
        }
        if (outputEditor) {
            outputEditor.destroy();
            outputEditor = null;
        }

        // Clear the DOM containers to prevent memory leaks
        if (inputEditorContainer) {
            inputEditorContainer.innerHTML = "";
        }
        if (outputEditorContainer) {
            outputEditorContainer.innerHTML = "";
        }

        // Wait for DOM update
        await tick();

        // Recreate editors with new language
        if (inputEditorContainer) {
            inputEditor = createTextFormatterEditor({
                parent: inputEditorContainer,
                doc: inputDoc,
                language: settings.language,
                readOnly: false,
                onChange: handleInputChange,
            });
        }

        if (outputEditorContainer) {
            outputEditor = createTextFormatterEditor({
                parent: outputEditorContainer,
                doc: outputDoc,
                language: settings.language,
                readOnly: true,
            });
        }

        // Clear flag after recreation is complete
        isRecreatingEditors = false;

        // Re-format with new language
        if (inputDoc) {
            performFormatting();
        }
    }

    // ============================================================================
    // FORMATTING OPERATIONS
    // ============================================================================

    function performFormatting(): void {
        isProcessing = true;
        errorMessage = "";

        try {
            const result = FormatterService.format(inputContent, settings.language, settings.operation, settings.config);

            if (result.success) {
                outputContent = result.output;
                updateOutputEditor(result.output);
            } else {
                errorMessage = result.error || "Formatting failed";
                outputContent = "";
                updateOutputEditor("");
            }
        } catch (error) {
            errorMessage = error instanceof Error ? error.message : String(error);
            outputContent = "";
            updateOutputEditor("");
        } finally {
            isProcessing = false;
        }
    }

    function updateOutputEditor(content: string): void {
        if (outputEditor) {
            const transaction = outputEditor.state.update({
                changes: {
                    from: 0,
                    to: outputEditor.state.doc.length,
                    insert: content,
                },
            });
            outputEditor.dispatch(transaction);
        }
    }

    // ============================================================================
    // EVENT HANDLERS
    // ============================================================================

    function handleLanguageChange(event: Event): void {
        const target = event.target as HTMLSelectElement;
        const newLanguage = target.value as Language;
        settings.language = newLanguage;

        // Update operation to first available for new language
        const operations = FormatterService.getOperationsForLanguage(newLanguage);
        if (operations.length > 0) {
            settings.operation = operations[0];
        }

        // Reset guard to trigger re-format with new language
        lastProcessedInput = "";
    }

    function handleOperationChange(event: Event): void {
        const target = event.target as HTMLSelectElement;
        settings.operation = target.value as Operation;
        lastProcessedInput = "";
        performFormatting();
    }

    function handleIndentSizeChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        settings.config.indentSize = parseInt(target.value, 10);
        lastProcessedInput = "";
        performFormatting();
    }

    function handleSortKeysChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        settings.config.sortKeys = target.checked;
        lastProcessedInput = "";
        performFormatting();
    }

    function handleClearInput(): void {
        inputContent = "";
        lastProcessedInput = "";
        if (inputEditor) {
            const transaction = inputEditor.state.update({
                changes: {
                    from: 0,
                    to: inputEditor.state.doc.length,
                    insert: "",
                },
            });
            inputEditor.dispatch(transaction);
        }
        outputContent = "";
        updateOutputEditor("");
        errorMessage = "";
    }

    function handleCopyOutput(): void {
        if (outputContent) {
            navigator.clipboard.writeText(outputContent).then(
                () => {
                    // Success - could show a toast notification
                    console.log("Copied to clipboard");
                },
                (err) => {
                    console.error("Failed to copy:", err);
                },
            );
        }
    }

    // ============================================================================
    // COMPUTED VALUES
    // ============================================================================

    const availableOperations = $derived(FormatterService.getOperationsForLanguage(settings.language));

    const showConfigOptions = $derived(FormatterService.requiresConfig(settings.language, settings.operation));
</script>

<div class="text-formatter">
    <!-- Control Bar -->
    <div class="text-formatter-controls">
        <div class="columns is-mobile is-multiline is-variable is-1 mb-0">
            <!-- Language Selection -->
            <div class="column is-narrow">
                <div class="field is-horizontal">
                    <div class="field-label is-small">
                        <label class="label is-small" for="language-select">Language</label>
                    </div>
                    <div class="field-body">
                        <div class="field">
                            <div class="control">
                                <div class="select is-small">
                                    <select id="language-select" bind:value={settings.language} onchange={handleLanguageChange}>
                                        <option value="json">JSON</option>
                                        <option value="xml">XML</option>
                                        <option value="html">HTML</option>
                                        <option value="text">TEXT</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Operation Selection -->
            <div class="column is-narrow">
                <div class="field is-horizontal">
                    <div class="field-label is-small">
                        <label class="label is-small" for="operation-select">Operation</label>
                    </div>
                    <div class="field-body">
                        <div class="field">
                            <div class="control">
                                <div class="select is-small">
                                    <select id="operation-select" bind:value={settings.operation} onchange={handleOperationChange}>
                                        {#each availableOperations as op}
                                            <option value={op}>
                                                {FormatterService.getOperationLabel(op)}
                                            </option>
                                        {/each}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Additional Options (shown conditionally) -->
            {#if showConfigOptions}
                <!-- Indent Size -->
                <div class="column is-narrow">
                    <div class="field is-horizontal">
                        <div class="field-label is-small">
                            <label class="label is-small" for="indent-size">Indent</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <div class="control">
                                    <input id="indent-size" class="input is-small" type="number" min="1" max="8" style="width: 60px;" bind:value={settings.config.indentSize} oninput={handleIndentSizeChange} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sort Keys (JSON only) -->
                {#if settings.language === "json"}
                    <div class="column is-narrow">
                        <div class="field">
                            <div class="control">
                                <label class="checkbox is-small">
                                    <input id="sort-keys" type="checkbox" bind:checked={settings.config.sortKeys} onchange={handleSortKeysChange} />
                                    <span class="is-size-7 ml-1">Sort Keys</span>
                                </label>
                            </div>
                        </div>
                    </div>
                {/if}
            {/if}

            <!-- Action Buttons -->
            <div class="column is-narrow">
                <div class="field is-grouped">
                    <p class="control">
                        <button id="clear-button" class="button is-small is-danger is-light" onclick={handleClearInput}>
                            <span class="icon is-small">
                                <i class="fas fa-eraser"></i>
                            </span>
                            <span>Clear</span>
                        </button>
                    </p>
                    <p class="control">
                        <button class="button is-small is-info is-light" onclick={handleCopyOutput}>
                            <span class="icon is-small">
                                <i class="fas fa-copy"></i>
                            </span>
                            <span>Copy</span>
                        </button>
                    </p>
                </div>
            </div>
        </div>
    </div>

    <!-- Error Message -->
    {#if errorMessage}
        <div class="notification is-danger is-light is-small py-2 mb-3">
            <button class="delete is-small" aria-label="Dismiss error message" onclick={() => (errorMessage = "")}></button>
            <strong>Error:</strong>
            {errorMessage}
        </div>
    {/if}

    <!-- Editor Panes -->
    <div class="text-formatter-editors columns is-mobile">
        <!-- Input Editor -->
        <div class="column is-half-tablet is-full-mobile">
            <div class="text-formatter-editor-header">
                <h3 class="title is-6 mb-0 has-text-weight-semibold">Input</h3>
            </div>
            <div class="text-formatter-editor-wrapper">
                <div bind:this={inputEditorContainer} class="text-formatter-editor"></div>
            </div>
        </div>

        <!-- Output Editor -->
        <div class="column is-half-tablet is-full-mobile">
            <div class="text-formatter-editor-header">
                <h3 class="title is-6 mb-0 has-text-weight-semibold">Output</h3>
            </div>
            <div class="text-formatter-editor-wrapper">
                <div bind:this={outputEditorContainer} class="text-formatter-editor"></div>
            </div>
        </div>
    </div>
</div>

<style>
    .text-formatter {
        width: 100%;
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .text-formatter-controls {
        padding: 0.75rem;
        background-color: #f5f5f5;
        border: 1px solid #dbdbdb;
        border-radius: 4px;
        flex-shrink: 0;
        margin-bottom: 0.75rem;
    }

    .text-formatter-controls :global(.columns) {
        align-items: center;
    }

    .text-formatter-controls :global(.column) {
        display: flex;
        align-items: center;
    }

    .text-formatter-controls :global(.field-label) {
        flex-grow: 0;
        margin-right: 0.5rem;
        padding-top: 0;
        display: flex;
        align-items: center;
    }

    .text-formatter-controls :global(.label) {
        margin-bottom: 0;
    }

    .text-formatter-controls :global(.field.is-horizontal) {
        display: flex;
        align-items: center;
        margin-bottom: 0;
    }

    .text-formatter-controls :global(.field.is-horizontal .field-label) {
        padding-top: 0;
        margin-bottom: 0;
        display: flex;
        align-items: center;
    }

    .text-formatter-controls :global(.field) {
        margin-bottom: 0;
    }

    .text-formatter-controls :global(.field-body) {
        display: flex;
        align-items: center;
    }

    .text-formatter-controls :global(.checkbox) {
        display: flex;
        align-items: center;
        margin-top: 0;
    }

    .text-formatter-controls :global(.field.is-grouped) {
        margin-bottom: 0;
        display: flex;
        align-items: center;
    }

    .text-formatter-controls :global(.control) {
        display: flex;
        align-items: center;
    }

    .text-formatter-controls :global(.select),
    .text-formatter-controls :global(.select select) {
        display: block;
    }

    .text-formatter-controls :global(.input),
    .text-formatter-controls :global(.button) {
        display: inline-flex;
    }

    .text-formatter-editors {
        flex: 1;
        display: flex;
        min-height: 0;
        margin: 0 -0.375rem;
    }

    .text-formatter-editors .column {
        display: flex;
        flex-direction: column;
        padding: 0 0.375rem;
    }

    .text-formatter-editor-header {
        padding: 0.375rem 0.5rem;
        background-color: #f5f5f5;
        border: 1px solid #dbdbdb;
        border-bottom: none;
        border-radius: 4px 4px 0 0;
    }

    .text-formatter-editor-wrapper {
        border: 1px solid #dbdbdb;
        border-radius: 0 0 4px 4px;
        overflow: hidden;
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    .text-formatter-editor {
        flex: 1;
        overflow: auto;
        min-height: 400px;
    }

    /* Make editors fill available space */
    .text-formatter-editor :global(.cm-editor) {
        height: 100%;
    }

    .text-formatter-editor :global(.cm-scroller) {
        overflow: auto;
    }

    /* Ensure line numbers always show */
    .text-formatter-editor :global(.cm-gutters) {
        min-height: 400px;
    }

    /* Read-only editor styling */
    .text-formatter-editor :global(.cm-readonly) {
        background-color: #fafafa;
    }

    /* Mobile adjustments */
    @media screen and (max-width: 768px) {
        .text-formatter-controls {
            padding: 0.5rem;
        }

        .text-formatter-controls :global(.columns) {
            margin-bottom: 0;
        }

        .text-formatter-controls :global(.field.is-horizontal) {
            display: block;
        }

        .text-formatter-controls :global(.field-label) {
            margin-bottom: 0.25rem;
            text-align: left;
        }

        .text-formatter-editors {
            flex-direction: column;
            gap: 0.5rem;
            padding: 0.5rem;
        }

        .text-formatter-editor {
            min-height: 250px;
        }

        .text-formatter-editor :global(.cm-gutters) {
            min-height: 250px;
        }
    }
</style>
